import React, {FC} from 'react';
import styled from 'styled-components';

import {ClickEventHandler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {DateCell} from '../../components/common/table/cells/dateCell';
import {EmptyCell} from '../../components/common/table/cells/emptyCell';
import {ExpandableResourceCellInheritableProps} from '../../components/common/table/cells/expandableResourceCell';
import {TableColumn} from '../../components/common/table/tableColumn';
import {TableRow, TableRowRenderOptions} from '../../components/common/table/tableRow';
import {AnswerFolderModel} from '../../models/api/answerFolderModel';
import {AnswerModel, RAnswer} from '../../models/api/answerModel';
import {SettingsAnswersUrl} from '../../models/url/settingsUrlModel';
import {SettingsResourcesTableColumnsEnum} from '../common/settingsResourcesCommon';
import {SettingsAnswerInboxesCellContainer} from './settingsAnswerInboxesCell';
import {SettingsAnswersTableFolderNameCellContainer} from './settingsAnswersTableFolderNameCellContainer';
import {SettingsAnswersTableNameCellContainer} from './settingsAnswersTableNameCellContainer';

/*
 * Props.
 */

interface SettingsAnswerTableRowProps
  extends ExpandableResourceCellInheritableProps<AnswerModel | AnswerFolderModel> {
  isSelected: boolean;
  tableColumns: ReadonlyArray<TableColumn<SettingsResourcesTableColumnsEnum>>;
  options: TableRowRenderOptions;
  isPreviousSiblingSelected: boolean;
  isNextSiblingSelected: boolean;
  isInMultiSelectMode?: boolean;
  onNameCellClick: ClickEventHandler;
  onCellClick: ClickEventHandler;
  onCheckBoxToggle: ClickEventHandler;
  answerOrFolder: AnswerModel | AnswerFolderModel;
  isInSearch: boolean;
  settingsAnswersUrl: SettingsAnswersUrl;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}
const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Component.
 */

export const SettingsAnswerTableRow: FC<SettingsAnswerTableRowProps> = (props) => {
  const {
    isSelected,
    tableColumns,
    options,
    isPreviousSiblingSelected,
    isNextSiblingSelected,
    isInMultiSelectMode,
    onCheckBoxToggle,
    answerOrFolder,
    settingsAnswersUrl,
  } = props;

  const nameCellRenderer = (
    index: number,
    column: TableColumn<SettingsResourcesTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    if (RAnswer.is(answerOrFolder)) {
      return (
        <SettingsAnswersTableNameCellContainer
          isInSearch={props.isInSearch}
          hasHover={props.hasHover}
          hasChevron={props.hasChevron}
          onChevronClick={props.onChevronClick}
          renderActionsDropdown={props.renderActionsDropdown}
          onActionsDropdownOpened={props.onActionsDropdownOpened}
          key={column.id}
          value={answerOrFolder}
          settings={column.settings}
          isHovering={isHovering}
          size={options.size}
          index={index}
          onClick={(e) => {
            e.preventDefault();
            props.onNameCellClick(e);
          }}
          isSelected={isSelected}
          shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
          shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          shouldShowStickyGradient={shouldShowStickyGradient}
          parentFolderId={(settingsAnswersUrl.inFolder && settingsAnswersUrl.folderId) || undefined}
        />
      );
    }

    return (
      <SettingsAnswersTableFolderNameCellContainer
        isInSearch={props.isInSearch}
        hasHover={props.hasHover}
        hasChevron={props.hasChevron}
        isExpanded={props.isExpanded}
        onChevronClick={props.onChevronClick}
        renderActionsDropdown={props.renderActionsDropdown}
        onActionsDropdownOpened={props.onActionsDropdownOpened}
        key={column.id}
        value={answerOrFolder}
        settings={column.settings}
        isHovering={isHovering}
        size={options.size}
        index={index}
        onClick={(e) => {
          e.preventDefault();
          props.onNameCellClick(e);
        }}
        isSelected={isSelected}
        shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
        shouldRemoveBottomBorderRadius={isNextSiblingSelected}
        shouldShowStickyGradient={shouldShowStickyGradient}
        parentFolderId={(settingsAnswersUrl.inFolder && settingsAnswersUrl.folderId) || undefined}
      />
    );
  };

  const cellRenderer = (
    index: number,
    column: TableColumn<SettingsResourcesTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    const emptyCell = (
      <EmptyCell
        key={column.type}
        settings={column.settings}
        isHovering={isHovering}
        size={options.size}
        index={index}
        isSelected={isSelected}
        shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
        shouldRemoveBottomBorderRadius={isNextSiblingSelected}
      />
    );

    switch (column.type) {
      case SettingsResourcesTableColumnsEnum.NAME: {
        return nameCellRenderer(index, column, isHovering, shouldShowStickyGradient);
      }
      case SettingsResourcesTableColumnsEnum.AVAILABLE_IN: {
        if (!RAnswer.is(answerOrFolder)) {
          return emptyCell;
        }

        return (
          <SettingsAnswerInboxesCellContainer
            key={column.id}
            value={answerOrFolder}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      }

      case SettingsResourcesTableColumnsEnum.DATE_CREATED: {
        return (
          <DateCell
            key={column.id}
            value={answerOrFolder.createdAt}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
            customFormat={{
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            }}
          />
        );
      }

      default: {
        return emptyCell;
      }
    }
  };

  return (
    <StyledTableRow
      hasHover
      isSelected={isSelected}
      onCheckBoxClick={(e) => {
        e.preventDefault();
        onCheckBoxToggle(e);
      }}
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      onSelect={(e) => {
        if (e.defaultPrevented) {
          return;
        }

        props.onCellClick(e);
      }}
      $minHeight={options.tableRowHeight}
      shouldShowRightGradient={options.shouldShowRightGradient}
      shouldAllowMultiSelect={options.shouldAllowMultiSelect}
      isCheckboxesVisible={isInMultiSelectMode}
    />
  );
};
